import {
  Box,
  Button,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useMemo, useCallback, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hook';
import {
  updateField,
  addIssue,
  addIssues,
  removeIssue,
} from 'store/SquaresStore/WorkOrderManagement/WorkOrderCreationFormState.slice';
import {
  FormLabel,
  MemoizedDatePicker,
  MemoizedTextField,
  MemoizedTimeSelectorWithUnit,
} from './CustomInput';
import { ISSUE_TYPE_OPTIONS, PRIORITY_OPTIONS } from './meta/metadata';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { getUtcISOStringOfSelectedTz } from 'utils/helpers/dateTime';
import { FlexBox } from 'components/Containers/FlexBox';
import { Delete } from '@mui/icons-material';
import { formatNumber } from '../../helpers/formatters';
import { get } from 'lodash';
import type { WorkOrderIssue } from 'types/Squares/WorkOrderManagement/work.order.creation.form.types';
import type { ServiceView } from 'types/survivalAnalysis.types';
import type { ComponentDetails } from 'types/squares.type';
import dayjs from 'dayjs';
import useAppTheme from 'hooks/useAppTheme';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const TechnicianSelector = ({ name }: { name: string }) => {
  const externalUserList = useAppSelector(
    (state) => state.externalUserReducer?.externalUserList
  );
  const userOptions = useMemo(() => {
    return (
      externalUserList?.map((el) => ({
        label: `${el?.firstName ?? ''} ${el?.lastName ?? ''}`,
        value: el?.userId,
      })) || []
    );
  }, [externalUserList]);

  return (
    <MemoizedTextField
      name={name}
      select
      fullWidth
      size="small"
      options={userOptions}
    />
  );
};

const EstimatedTimeInput = ({
  data,
  index,
}: {
  data: WorkOrderIssue;
  index: number;
}) => {
  const dispatch = useAppDispatch();

  const timeValue = data.estimatedTime;
  const timeUnit = data.estimatedTimeUnit as 'Hours' | 'Days';

  const name = `components[${index}].estimatedTime`;
  const error = useAppSelector((state) =>
    get(state.sqauresWorkOrderCreationFormReducer.errors, name, '')
  );

  const handleTimeUnitChange = (
    _event: React.MouseEvent<HTMLElement>,
    newUnit: 'Hours' | 'Days' | null
  ) => {
    if (newUnit !== null) {
      dispatch(
        updateField({
          key: `components[${index}].estimatedTimeUnit`,
          value: newUnit,
        })
      );
    }
  };

  const handleTimeValueChange = (amount: number) => {
    const newValue = Math.max(0, timeValue + amount);
    dispatch(
      updateField({
        key: `components[${index}].estimatedTime`,
        value: newValue,
      })
    );
  };

  const handleTimeInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = formatNumber(event.target.value, '');
    if (value === '') {
      dispatch(
        updateField({
          key: `components[${index}].estimatedTimeUnit`,
          value: '',
        })
      );
    } else if (typeof value === 'number') {
      if (!isNaN(value) && value >= 0) {
        dispatch(
          updateField({ key: `components[${index}].estimatedTime`, value })
        );
      }
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 3,
      }}
    >
      <MemoizedTimeSelectorWithUnit
        name="estimatedTime"
        // label={`${data.description} - Estimated Time`}
        required
        value={timeValue}
        unit={timeUnit}
        onValueChange={handleTimeInputChange}
        onUnitChange={handleTimeUnitChange}
        onIncrement={() => {
          handleTimeValueChange(1);
        }}
        onDecrement={() => {
          handleTimeValueChange(-1);
        }}
        error={error}
      />
    </Box>
  );
};

const AssetIdSelector = ({ asset }: { asset: ServiceView | null }) => {
  const dispatch = useAppDispatch();
  const assets = useAppSelector((state) => state.assetReducer?.assets);
  const value = useAppSelector(
    (store) => store.sqauresWorkOrderCreationFormReducer.values.assetId
  );
  const error = useAppSelector(
    (store) => store.sqauresWorkOrderCreationFormReducer.errors.assetId
  );

  const assetOptions = useMemo(() => {
    return (
      assets?.map((el) => ({
        assetId: el.assetId,
        label: el.bumperNumber,
        make: el.make,
        model: el.model,
        assetType: el.assetType,
      })) || []
    );
  }, [assets]);

  useEffect(() => {
    if (!value && asset?.item_id && assetOptions.length > 0) {
      const selectedAsset = assetOptions.find(
        (opt) => opt.label === asset.item_id
      );

      if (selectedAsset) {
        dispatch(updateField({ key: 'assetId', value: selectedAsset.assetId }));
        dispatch(
          updateField({ key: 'assetType', value: selectedAsset.assetType })
        );
        dispatch(updateField({ key: 'make', value: selectedAsset.make }));
        dispatch(updateField({ key: 'model', value: selectedAsset.model }));
      }
    }
  }, [value, asset?.item_id, assetOptions, dispatch]);

  interface AssetOption {
    assetId: string;
    label: string;
    make: string;
    model: string;
    assetType: string;
  }

  interface AssetChangeEvent {
    target: {
      value: string;
    };
  }

  const handleAssetChange = useCallback(
    (event: AssetChangeEvent) => {
      const val = event.target.value;
      const selectedAsset: AssetOption | undefined = assetOptions.find(
        (opt: AssetOption) => opt.assetId === val
      );
      dispatch(updateField({ key: 'assetId', value: val }));
      dispatch(
        updateField({ key: 'assetType', value: selectedAsset?.assetType ?? '' })
      );
      dispatch(updateField({ key: 'make', value: selectedAsset?.make ?? '' }));
      dispatch(
        updateField({ key: 'model', value: selectedAsset?.model ?? '' })
      );
    },
    [dispatch, assetOptions]
  );

  return (
    <TextField
      size="small"
      select
      fullWidth
      value={value}
      onChange={handleAssetChange}
      error={!!error}
      helperText={error}
      SelectProps={{ MenuProps: { sx: { maxHeight: 400 } } }}
    >
      {assetOptions.map((option) => (
        <MenuItem key={option.assetId} value={option.assetId}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );
};

const CreationDateField = () => {
  const { timezoneBasedOnDisplayMode } = useTimeDisplayMode();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (timezoneBasedOnDisplayMode) {
      const utcDate = getUtcISOStringOfSelectedTz({
        startOfDay: true,
        tz: timezoneBasedOnDisplayMode,
      });
      dispatch(updateField({ key: 'creationDate', value: utcDate }));
    }
  }, [timezoneBasedOnDisplayMode]);

  return (
    <MemoizedDatePicker
      name={`creationDate`}
      readOnly
      slotProps={{ textField: { size: 'small', fullWidth: true } }}
    />
  );
};

// Description field with counter
const DescriptionField = ({
  index,
  value,
}: {
  index: number;
  value: string;
}) => {
  return (
    <Box>
      <Box display="flex" justifyContent="space-between">
        {/* <FormLabel required>Description</FormLabel> */}
        {/* <Typography fontSize="12px">{`(${value?.length || 0}/550)`}</Typography> */}
      </Box>
      <MemoizedTextField
        size="small"
        multiline
        maxRows={2}
        fullWidth
        name={`components[${index}].description`}
        inputProps={{ maxLength: 550 }}
      />
    </Box>
  );
};

const WorkOrderCreationStep = ({
  defaultComponents,
  asset,
}: {
  defaultComponents: ComponentDetails[];
  asset: ServiceView | null;
}) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const hasInitialized = useRef(false);
  const dispatch = useAppDispatch();
  const { values } = useAppSelector(
    (state) => state.sqauresWorkOrderCreationFormReducer
  );

  const initialDefaultComponents = defaultComponents.map((item) => {
    return {
      componentId: item.component,
      subComponentId: item.sub_component,
      plannedStartDate: dayjs().format('MM/DD/YYYY'),
      plannedEndDate: dayjs().format('MM/DD/YYYY'),
    };
  });

  const useInitial =
    values.components.length === 1 && initialDefaultComponents.length > 0;
  const components = useInitial ? initialDefaultComponents : values.components;

  useEffect(() => {
    if (hasInitialized.current) return;

    if (
      defaultComponents.length > 0 &&
      values.components.length === 1 &&
      !values.components[0].componentId
    ) {
      hasInitialized.current = true;

      if (values.components[0]?.id) {
        dispatch(removeIssue(values.components[0].id));
      }

      const initialComponents = defaultComponents.map((item) => ({
        componentId: item.component,
        subComponentId: item.sub_component,
        plannedStartDate: dayjs().format('MM/DD/YYYY'),
        plannedEndDate: dayjs().format('MM/DD/YYYY'),
      }));

      dispatch(addIssues(initialComponents));
      dispatch(updateField({ key: 'maintenanceType', value: 'Inspect' }));
    }
  }, [defaultComponents, values.components, dispatch]);

  return (
    <Box
      width="100%"
      height="100%"
      overflow="auto"
      sx={{ maxHeight: 'calc(100vh - 200px)' }}
    >
      {/* Header */}
      <FlexBox sx={{ justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" fontSize={16} fontWeight="medium">
          {t('work_order_creation')}
        </Typography>
        <Typography variant="caption" fontSize={12}>
          <span style={{ color: 'red' }}>*</span> {t('mandatory_fields_2')}
        </Typography>
      </FlexBox>
      <Typography
        variant="body2"
        fontSize={12}
        color="text.secondary"
        sx={{ mb: 4 }}
      >
        {t('start_by_entering_all_key_details_related_to_the_asset_repor')}
      </Typography>

      {/* Asset Details */}
      <Box display="flex" flexDirection="column" gap={2} mb={3}>
        {/* Single row with all fields */}
        <Box
          display="flex"
          gap={2}
          flexDirection={{ xs: 'column', sm: 'row' }}
          sx={{ overflowX: 'auto' }}
        >
          <Box flex={1.2} minWidth="140px">
            <FormLabel required>{t('asset_id')}</FormLabel>
            <AssetIdSelector asset={asset} />
          </Box>
          <Box flex={1.2} minWidth="140px">
            <FormLabel>{t('make_model')}</FormLabel>
            {/* FIX: Using plain TextField instead of MemoizedTextField */}
            <TextField
              size="small"
              fullWidth
              value={`${values.make || ''} - ${values.model || ''}`}
              InputProps={{ readOnly: true }}
            />
          </Box>
          <Box flex={1} minWidth="120px">
            <FormLabel>{t('asset_type')}</FormLabel>
            <TextField
              size="small"
              fullWidth
              value={values.assetType || ''}
              InputProps={{ readOnly: true }}
            />
          </Box>
          <Box flex={1} minWidth="120px">
            <FormLabel required>{t('priority')}</FormLabel>
            <MemoizedTextField
              size="small"
              select
              fullWidth
              name={`priority`}
              options={PRIORITY_OPTIONS}
            />
          </Box>
          <Box flex={1} minWidth="140px">
            <FormLabel>{t('wo_creation_date')}</FormLabel>
            <CreationDateField />
          </Box>
          <Box flex={1.2} minWidth="140px">
            <FormLabel required>{t('maintenance_type')}</FormLabel>
            <MemoizedTextField
              size="small"
              select
              fullWidth
              name={'maintenanceType'}
              options={ISSUE_TYPE_OPTIONS}
              sx={{ borderRadius: '5px' }}
            />
          </Box>
          <Box flex={1.2} minWidth="140px">
            <FormLabel>{t('technician_assigned')}</FormLabel>
            <TechnicianSelector name={`assignedUserId`} />
          </Box>
        </Box>
      </Box>

      {/* Horizontal Scroll Indicator */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          mb: 2,
          p: 1,
          backgroundColor: (theme) => theme.palette.background.paper,
          borderRadius: 1,
          border: '1px solid #FFFFFF26',
        }}
      >
        <Typography variant="caption" color="text.secondary" fontSize={12}>
          {t('scroll_horizontally_to_view_all_columns')}
        </Typography>
      </Box>

      {/* Issues Table Header */}
      <Box
        sx={{
          overflowX: 'auto',
          overflowY: 'auto',
          maxHeight: '400px',
          border: '1px solid #FFFFFF26',
          borderRadius: 1,
        }}
      >
        {/* Header */}
        <Box
          display="flex"
          sx={{
            minWidth: '1728px', // Sum of column widths (280+180+200+200+210+200+240+260+360+70) + 9 gaps * 2px
            background: palette.toolBarBackground,
            p: 1.5,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            gap: 2,
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          {[
            {
              label: (
                <>
                  {t('description')} <span style={{ color: 'red' }}>*</span> 550
                </>
              ),
              width: 280,
            },
            { label: t('part_number'), width: 180 },
            { label: t('component'), width: 200 },
            { label: t('sub_component_4'), width: 200 },
            {
              label: (
                <>
                  {t('planned_start_date')}{' '}
                  <span style={{ color: 'red' }}>*</span>
                </>
              ),
              width: 210,
            },
            {
              label: (
                <>
                  {t('planned_end_date')}{' '}
                  <span style={{ color: 'red' }}>*</span>
                </>
              ),
              width: 200,
            },
            { label: t('estimated_time'), width: 360 },
            { label: t('actions'), width: 70 },
          ].map((col, i) => (
            <Typography
              key={i}
              fontSize={13}
              fontWeight="bold"
              sx={{
                color: '#fff',
                width: col.width,
                textTransform: 'uppercase',
              }}
            >
              {col.label}
            </Typography>
          ))}
        </Box>

        {/* Issues Rows */}
        {components.map((issue: WorkOrderIssue, idx: number) => (
          <Box
            key={idx}
            display="flex"
            sx={{
              minWidth: '1728px', // Consistent with header
              borderTop: '1px solid #FFFFFF26',
              p: 1.5,
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box sx={{ width: 280 }}>
              <DescriptionField index={idx} value={issue.description} />
            </Box>
            <Box sx={{ width: 180 }}>
              <MemoizedTextField
                size="small"
                fullWidth
                name={`components[${idx}].partNumber`}
              />
            </Box>
            <Box sx={{ width: 200 }}>
              <MemoizedTextField
                size="small"
                fullWidth
                name={`components[${idx}].componentId`}
              />
            </Box>
            <Box sx={{ width: 200 }}>
              <MemoizedTextField
                size="small"
                fullWidth
                name={`components[${idx}].subComponentId`}
              />
            </Box>
            <Box sx={{ width: 210 }}>
              <MemoizedDatePicker
                name={`components[${idx}].plannedStartDate`}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Box>
            <Box sx={{ width: 200 }}>
              <MemoizedDatePicker
                name={`components[${idx}].plannedEndDate`}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Box>
            <Box sx={{ width: 360 }}>
              <EstimatedTimeInput index={idx} data={issue} />
            </Box>
            <Box sx={{ width: 70, display: 'flex', gap: 1 }}>
              <IconButton
                color="error"
                size="small"
                onClick={() => dispatch(removeIssue(issue.id))}
                disabled={components.length === 1}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Add More */}
      <FlexBox sx={{ justifyContent: 'center', mt: 2, mb: 3 }}>
        <Button variant="outlined" onClick={() => dispatch(addIssue())}>
          {t('add_more')}
        </Button>
      </FlexBox>
    </Box>
  );
};

export default WorkOrderCreationStep;
